import { useTranslation } from 'react-i18next'
import { LandingPage } from '../../components'
import LinkButton from '@/components/ui/link-button'
import { imageUrl } from '../../helpers'
import desktopClientData from '../../../../../config/static_data/desktop_client.json'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'

const DesktopClientPage = () => {
  const { t } = useTranslation()
  const gameVersion = useGameVersion()
  const hasLoot = gameVersion.has('loot')
  const macUrl = desktopClientData.installer_urls.mac
  const windowsUrl = desktopClientData.installer_urls.windows
  const userAgent = navigator.userAgent.toLowerCase()

  return (
    <LandingPage
      title={t('homepage.desktop-title')}
      description={t(
        gameVersion.has('blizzard_api')
          ? 'homepage.desktop-description'
          : 'homepage.desktop-description-companion',
      )}
    >
      {hasLoot ? (
        <p>
          The wowaudit desktop client allows you to automatically synchronise
          wishlist data to RCLootCouncil and synchronise the RCLootCouncil loot
          history back to wowaudit. It also lets you synchronise guild data
          (like the state of your neighborhood) back to the wowaudit website.
        </p>
      ) : (
        <p>
          The wowaudit desktop client, together with the wowaudit companion
          addon, synchronises your in-game data to the wowaudit website. This
          includes your guild roster, ranks and notes, and the level, gear and
          professions of your characters.
        </p>
      )}

      <h3 className='mt-4'>Installation</h3>
      <p>
        Download the client for your operating system below. Once downloaded,
        simply run the installer! Updates can be downloaded from within the
        client, and the client can be configured to start up automatically when
        you boot your computer (it&apos;s very lightweight and should not be
        noticeable at all).
      </p>

      <LinkButton
        to={userAgent.includes('mac') ? macUrl : windowsUrl}
        variant='outline'
        className='flex items-center gap-1 mt-6'
      >
        <img
          src={
            userAgent.includes('mac')
              ? imageUrl('apple-logo.svg')
              : imageUrl('windows-logo.svg')
          }
          className='w-6 h-6 min-w-6 min-h-6 dark:invert'
          alt='Download'
        />
        Download {userAgent.includes('mac') ? '(macOS)' : '(Windows)'}
      </LinkButton>
      <div className='text-center mt-1.5 text-xs'>
        or download the{' '}
        <a
          href={userAgent.includes('mac') ? windowsUrl : macUrl}
          className='text-primary hover:text-primary/90'
        >
          {userAgent.includes('mac') ? 'Windows' : 'macOS'}
        </a>{' '}
        installer
      </div>

      <h2 className='mt-4'>Usage</h2>
      {hasLoot ? (
        <p>
          There are two ways to connect the client. Log in as a player and
          authorize the client with your wowaudit account to sync in-game data
          for your guild and characters. For the RCLootCouncil integration you
          need a team API key instead: if you have admin access to a team, visit
          the API page on the website and paste the API key into the client.
        </p>
      ) : (
        <p>
          Log in as a player and authorize the client with your wowaudit account
          to sync in-game data for your guild and characters. Team admins can
          also connect the client with the API key from the API page.
        </p>
      )}

      {hasLoot ? (
        <>
          <h4 className='mt-4'>RCLootCouncil integration</h4>
          <p className='mb-3'>
            Install the RCLootCouncil plugin for wowaudit at{' '}
            <a
              href='https://www.curseforge.com/wow/addons/rclootcouncil-wowaudit'
              className='text-primary hover:text-primary/90'
              target='_blank'
              rel='noreferrer'
            >
              CurseForge
            </a>{' '}
            or{' '}
            <a
              href='https://addons.wago.io/addons/rclootcouncil-wowaudit'
              className='text-primary hover:text-primary/90'
              target='_blank'
              rel='noreferrer'
            >
              Wago
            </a>
            . Since it is a plugin, you also need the main RCLootCouncil addon.
            After that, you need to point the client to the path where the
            plugin addon is installed. Once the configuration is active,
            everything else will happen automatically. Your data will be
            synchronised every 15 minutes, but you can also sync manually
            whenever you like. Remember that you need to <code>/reload</code>{' '}
            your game after data has changed, otherwise it will not be reflected
            in the game.
          </p>
          <p>
            Only the master looter of your raid team has to run the client, the
            wishlist data will be shared with all other players using the
            RCLootCouncil - wowaudit plugin automatically. It can still be
            useful to have multiple players in the raid running the client,
            since the loot history of one player will likely not cover
            everything (unless that player is always in the raid).
          </p>
        </>
      ) : null}

      <h4 className='mt-4'>Synchronising in-game data</h4>
      <p className='mb-3'>
        Install the wowaudit companion addon at{' '}
        <a
          href='https://www.curseforge.com/wow/addons/wowaudit-companion'
          className='text-primary hover:text-primary/90'
          target='_blank'
          rel='noreferrer'
        >
          CurseForge
        </a>{' '}
        or{' '}
        <a
          href='https://addons.wago.io/addons/wowaudit-companion'
          className='text-primary hover:text-primary/90'
          target='_blank'
          rel='noreferrer'
        >
          Wago
        </a>
        . After that, you need to point the client to the path where the addon
        is installed. Once the configuration is active, synced data will
        automatically be sent to the website whenever you log out or /reload.
      </p>

      {gameVersion.has('housing') ? (
        <p>
          Occupancy data for your guild neighborhood will be synced
          automatically whenever you zone in to your neighborhood. If your guild
          has multiple subdivisions, data from other subdivisions will only be
          synced each time you visit them. If you have visited them at least
          once, you will be able to sync all subdivions at once by opening the
          bulletin board in your neighborhood.
        </p>
      ) : null}

      <p>
        If you need any more help, please reach out on{' '}
        <a
          href='https://discord.gg/86eUAFz'
          className='text-primary hover:text-primary/90'
        >
          Discord
        </a>
        .
      </p>
    </LandingPage>
  )
}

export default DesktopClientPage
